import {LitElement, html, css} from "lit";
import {SignalWatcher, signal} from "@lit-labs/preact-signals";
import {customElement} from "lit/decorators.js";
import {Html5Qrcode} from "html5-qrcode";
import {authedFetch} from "../../auth/auth-fetch.js";
import {morphedRead} from "../../auth/morphed-client.js";
import {iconCamera, iconStop, iconX, iconWarning, iconCheck, iconArrowLeft} from "../../utils/icons.js";

interface Evento {
	id: string;
	eventName: string;
	inscritos: string[];
	asistentes: string[];
}

interface Persona {
	id: string;
	name: string;
	email: string;
	emailInstitucional: string;
	puesto: string;
	empresa: string;
	tipoOrganizacion: string;
	estado: string;
	municipio: string;
	alergias: string;
	esMiembroClam: boolean;
	rol: string;
	gafete: string;
	foto: string;
}

type ResultStatus = "preview" | "already_checked_in" | "wrong_event" | "error" | "checked_in";

interface ScanResult {
	status: ResultStatus;
	persona?: Persona;
	evento?: {id: string; eventName: string};
	error?: string;
}

const QR_READER_ID = "clam-qr-reader";
const SIN_ALERGIAS = ["", "-", "NINGUNA", "NINGUNO", "NO", "N/A"];

@customElement("clam-scanner")
export class ClamScanner extends SignalWatcher(LitElement) {
	static styles = css`
		:host {
			display: block;
			font-family: var(--clam-font, 'Inter', system-ui, sans-serif);
			color: var(--clam-text, #333);
			padding: var(--clam-space-3, 12px);
			max-width: 560px;
			margin: 0 auto;
			box-sizing: border-box;
		}
		.header { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 12px; }
		.header h1 { font-size: 18px; color: var(--clam-darkgray, #424244); margin: 0; }
		.back-btn {
			background: none; border: 1px solid var(--clam-border, #e2e8f0);
			padding: 8px 12px; border-radius: 8px; cursor: pointer;
			font-size: 13px; font-family: var(--clam-font); color: var(--clam-text-muted, #6d6e71);
			min-height: 40px; white-space: nowrap;
		}
		.back-btn:hover { background: var(--clam-surface-soft, #f5f6f8); }

		.selector { margin-bottom: 12px; }
		.selector select {
			padding: 12px 14px; border: 1px solid var(--clam-border, #e2e8f0);
			border-radius: 10px; font-size: 16px; font-family: var(--clam-font);
			width: 100%; box-sizing: border-box; background: var(--clam-surface, #fff); min-height: 48px;
		}

		.scan-card {
			background: var(--clam-surface, #fff); border-radius: 14px;
			padding: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.10);
		}

		.btn {
			padding: 14px 20px; border: none; border-radius: 12px;
			font-size: 16px; font-family: var(--clam-font); cursor: pointer; font-weight: 600;
			min-height: 52px; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
			width: 100%; box-sizing: border-box;
		}
		.btn-primary { background: var(--clam-primary, #5a9d1e); color: #fff; }
		.btn-primary:hover { background: var(--clam-primary-hover, #4c8a18); }
		.btn-secondary { background: var(--clam-surface-soft, #f5f6f8); color: var(--clam-text, #333); border: 1px solid var(--clam-border, #e2e8f0); }
		.btn-danger { background: var(--clam-danger, #ea4335); color: #fff; }
		.btn:disabled { opacity: 0.5; cursor: not-allowed; }
		.btn-row { display: flex; gap: 8px; }

		.manual { margin-top: 10px; }
		.manual summary {
			cursor: pointer; font-size: 13px; color: var(--clam-text-muted, #6d6e71);
			padding: 6px 0; list-style: none; user-select: none;
		}
		.manual summary::-webkit-details-marker { display: none; }
		.manual-row { display: flex; gap: 8px; margin-top: 8px; align-items: stretch; }
		.manual-row input {
			flex: 1; min-width: 0; padding: 12px 14px; border: 2px solid var(--clam-border, #e2e8f0);
			border-radius: 10px; font-size: 16px; font-family: var(--clam-font); min-height: 48px; box-sizing: border-box;
		}
		.manual-row input:focus { outline: none; border-color: var(--clam-light-green, #6bbb24); }
		.manual-row .btn { width: auto; min-height: 48px; padding: 0 18px; }

		.result-box { padding: 14px; border-radius: 12px; margin-top: 12px; border: 2px solid transparent; }
		.result-success { background: rgba(107,187,36,0.10); border-color: var(--clam-light-green, #6bbb24); }
		.result-warning { background: #fff8e1; border-color: #ffc107; }
		.result-error { background: rgba(234,67,53,0.06); border-color: var(--clam-danger, #ea4335); }
		.result-box h3 { margin: 0 0 10px; font-size: 16px; display: flex; align-items: center; gap: 8px; }
		.result-box p { margin: 0; font-size: 13px; line-height: 1.45; color: var(--clam-text-muted, #6d6e71); }

		.persona-card { display: flex; flex-direction: column; gap: 12px; }
		.persona-head { display: flex; gap: 12px; align-items: center; }
		.persona-avatar {
			width: 56px; height: 56px; border-radius: 50%; background: var(--clam-darkgray, #424244);
			color: #fff; display: flex; align-items: center; justify-content: center;
			font-size: 20px; font-weight: 700; flex-shrink: 0;
		}
		.persona-name { font-size: 17px; font-weight: 700; color: var(--clam-text, #222); line-height: 1.2; }
		.persona-sub { font-size: 13px; color: var(--clam-text-muted, #6d6e71); margin-top: 2px; }
		.tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
		.tag { font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 999px; text-transform: uppercase; letter-spacing: .03em; }
		.tag.member { background: rgba(90,157,30,0.14); color: #3f7a12; }
		.tag.type { background: #eef1f5; color: #424244; }
		.tag.gafete { background: #1e3a5f; color: #fff; letter-spacing: .05em; }
		.detail-grid { display: grid; grid-template-columns: 1fr; gap: 4px 12px; }
		.detail-row { display: flex; justify-content: space-between; gap: 12px; font-size: 13px; }
		.detail-label { color: var(--clam-text-muted, #6d6e71); }
		.detail-value { color: var(--clam-text, #333); font-weight: 600; text-align: right; word-break: break-word; }
		.alergias-alert {
			display: flex; gap: 8px; align-items: flex-start; background: #fff3cd; border: 1px solid #ffe08a;
			color: #7a5d00; padding: 10px 12px; border-radius: 10px; font-size: 13px; font-weight: 600;
		}

		.stats {
			background: var(--clam-surface, #fff); border-radius: 14px;
			padding: 14px; box-shadow: 0 2px 8px rgba(0,0,0,0.10); margin-top: 12px;
		}
		.stats h2 { font-size: 15px; color: var(--clam-darkgray, #424244); margin: 0 0 8px; }
		.stat-row { display: flex; justify-content: space-between; align-items: baseline; padding: 6px 0; border-bottom: 1px solid #f0f0f0; }
		.stat-row:last-child { border-bottom: none; }
		.stat-label { color: var(--clam-text-muted, #6d6e71); font-size: 13px; }
		.stat-value { font-weight: 700; font-size: 18px; }
		.stat-value.green { color: var(--clam-light-green, #6bbb24); }
		.stat-value.orange { color: #ff9800; }
		.stat-value.blue { color: var(--clam-green, #5a9d1e); }

		@media (min-width: 768px) {
			:host { max-width: 1100px; padding: 24px; }
			.layout { display: flex; gap: 16px; align-items: flex-start; }
			.scan-card { flex: 1; min-width: 0; }
			.stats { width: 300px; flex-shrink: 0; margin-top: 0; }
			.detail-grid { grid-template-columns: 1fr 1fr; }
		}
	`;

	private eventos = signal<Evento[]>([]);
	private selectedEvento = signal<string>("");
	private scanInput = signal<string>("");
	private result = signal<ScanResult | null>(null);
	private confirming = signal(false);
	private stats = signal({inscritos: 0, asistentes: 0, noAsistieron: 0});
	private _qr: Html5Qrcode | null = null;
	private _camOverlay: HTMLDivElement | null = null;
	private _camReaderId = "";
	private _camSeq = 0;
	private _decoding = false;
	private _lastToken = "";

	async connectedCallback() {
		super.connectedCallback();
		try {
			const eventos = await morphedRead<Evento>("Clam Evento", {options: {limit: 50}});
			this.eventos.value = eventos;
			if (eventos.length > 0) {
				this.selectedEvento.value = eventos[0].id;
				this.updateStats();
			}
		} catch (e) {
			console.error("Failed to load eventos:", e);
		}
		this.requestUpdate();
	}

	disconnectedCallback() {
		void this.stopCamera();
		super.disconnectedCallback();
	}

	private async updateStats() {
		const ev = this.eventos.value.find((e) => e.id === this.selectedEvento.value);
		if (!ev) return;
		const inscritos = ev.inscritos?.length || 0;
		const asistentes = ev.asistentes?.length || 0;
		this.stats.value = {inscritos, asistentes, noAsistieron: Math.max(0, inscritos - asistentes)};
	}

	private _onEventoChange(e: Event): void {
		this.selectedEvento.value = (e.target as HTMLSelectElement).value;
		this._clearResult();
		this.updateStats();
		this.requestUpdate();
	}

	private _clearResult(): void {
		this.result.value = null;
		this._lastToken = "";
		this.scanInput.value = "";
		this._decoding = false;
		this.requestUpdate();
	}

	/** Lookup (preview) sin registrar. Disparado por cámara o input manual. */
	private async _lookup(token: string): Promise<void> {
		const t = token.trim();
		if (!t || t === this._lastToken) return;
		this._lastToken = t;
		this._decoding = false;
		try {
			const res = await authedFetch(`/api/checkin/scan`, {
				method: "POST",
				body: JSON.stringify({qrToken: t, eventoId: this.selectedEvento.value, confirm: false}),
				audience: "clam",
			});
			const json = (await res.json()) as ScanResult & {error?: string};
			if (!res.ok) {
				this.result.value = {status: json.status === "wrong_event" ? "wrong_event" : "error", error: json.error || "Error al escanear"};
			} else {
				this.result.value = json as ScanResult;
			}
		} catch (e) {
			console.error("[scanner] lookup", e);
			this.result.value = {status: "error", error: this._friendlyError(e)};
		}
		this.requestUpdate();
	}

	/** Confirma la asistencia (registra). */
	private async _confirm(): Promise<void> {
		if (!this._lastToken) return;
		this.confirming.value = true;
		try {
			const res = await authedFetch(`/api/checkin/scan`, {
				method: "POST",
				body: JSON.stringify({qrToken: this._lastToken, eventoId: this.selectedEvento.value, confirm: true}),
				audience: "clam",
			});
			const json = (await res.json()) as ScanResult & {error?: string};
			if (!res.ok) {
				this.result.value = {status: "error", error: json.error || "Error al confirmar"};
			} else {
				this.result.value = json as ScanResult;
				this.eventos.value = this.eventos.value.map((e) =>
					e.id === json.evento?.id && json.persona ? {...e, asistentes: [...(e.asistentes || []), json.persona.id]} : e,
				);
				this.updateStats();
			}
		} catch (e) {
			console.error("[scanner] confirm", e);
			this.result.value = {status: "error", error: this._friendlyError(e)};
		}
		this.confirming.value = false;
		this.requestUpdate();
	}

	private _manualScan(): void {
		void this._lookup(this.scanInput.value);
	}

	/** Mensaje accionable según el error real de getUserMedia. */
	private _cameraErrorMessage(e: unknown): string {
		const name = (e as {name?: string})?.name ?? "";
		const msg = e instanceof Error ? e.message : String(e);
		switch (name) {
			case "NotAllowedError":
			case "SecurityError":
				return "Permiso de cámara denegado. En Brave, desactivá los Shields (ícono del león) para este sitio; en otros navegadores, habilitá el permiso de cámara.";
			case "NotFoundError":
			case "OverconstrainedError":
				return "No se detectó cámara en este dispositivo. Usá la entrada manual (abajo).";
			case "NotReadableError":
				return "La cámara la está usando otra app. Cerrala e intentá de nuevo.";
			default:
				return `No se pudo iniciar la cámara: ${msg}. Usá la entrada manual.`;
		}
	}

	private _isNetworkError(e: unknown): boolean {
		return e instanceof TypeError && /fetch/i.test(e.message);
	}
	private _friendlyError(e: unknown): string {
		if (this._isNetworkError(e)) {
			return "Sin conexión con el servidor. La URL pública cambia en cada reinicio del servidor: si dejaste la pestaña abierta de antes, recargá la página o abrí la URL nueva. Luego reintentá.";
		}
		return e instanceof Error ? e.message : String(e);
	}

	private async _retry(): Promise<void> {
		const t = this._lastToken;
		if (!t) return;
		this._lastToken = ""; // saltear el guard de dedup para volver a intentar
		await this._lookup(t);
	}

	/* ===== Cámara: overlay html5-qrcode en document.body ===== */

	private _mountOverlay(): HTMLDivElement {
		if (this._camOverlay && document.body.contains(this._camOverlay)) {
			return document.getElementById(this._camReaderId) as HTMLDivElement;
		}
		// id único por apertura: evita estado residual del <video> de html5-qrcode
		// (causa del "pantalla negra" al re-escanear).
		const readerId = `${QR_READER_ID}-${this._camSeq++}`;
		this._camReaderId = readerId;
		const overlay = document.createElement("div");
		overlay.id = "clam-cam-overlay";
		overlay.style.cssText = "position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,.92);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;padding:16px;box-sizing:border-box;";
		const reader = document.createElement("div");
		reader.id = readerId;
		reader.style.cssText = "width:min(90vw,420px);border-radius:14px;overflow:hidden;background:#000;";
		const hint = document.createElement("div");
		hint.textContent = "Encuadrá el código QR dentro del recuadro";
		hint.style.cssText = "color:#fff;font:600 14px/1.3 'Inter',system-ui,sans-serif;text-align:center;";
		const cancel = document.createElement("button");
		cancel.type = "button";
		cancel.textContent = "Cancelar";
		cancel.style.cssText = "padding:12px 28px;background:#ea4335;color:#fff;border:none;border-radius:10px;font:600 15px 'Inter',system-ui,sans-serif;cursor:pointer;min-height:48px;";
		cancel.onclick = () => { void this.stopCamera(); };
		overlay.append(reader, hint, cancel);
		document.body.append(overlay);
		this._camOverlay = overlay;
		return reader;
	}

	private _unmountOverlay(): void {
		if (this._camOverlay && document.body.contains(this._camOverlay)) {
			document.body.removeChild(this._camOverlay);
		}
		this._camOverlay = null;
	}

	private async startCamera(): Promise<void> {
		this._decoding = false;
		const reader = this._mountOverlay();
		const readerId = this._camReaderId;
		if (!reader || !document.getElementById(readerId)) {
			this._unmountOverlay();
			this.result.value = {status: "error", error: "No se pudo iniciar la cámara. Usá la entrada manual."};
			this.requestUpdate();
			return;
		}
		try {
			this._qr = new Html5Qrcode(readerId);
			await this._qr.start(
				{facingMode: "environment"},
				{fps: 10, qrbox: {width: 250, height: 250}},
				(decoded: string) => {
					if (this._decoding) return;
					this._decoding = true;
					void this.stopCamera().then(() => this._lookup(decoded));
				},
				() => {},
			);
		} catch (e) {
			this._unmountOverlay();
			this.result.value = {status: "error", error: this._cameraErrorMessage(e)};
			this.requestUpdate();
		}
	}

	private async stopCamera(): Promise<void> {
		// capturar el <video> antes de clear() para liberar la cámara a mano
		// (html5-qrcode a veces no suelta el track → al reabrir da video negro).
		const video = (this._camOverlay?.querySelector("video") as HTMLVideoElement | null) ?? null;
		const stream = (video?.srcObject as MediaStream | null) ?? null;
		if (this._qr) {
			try { await this._qr.stop(); } catch { /* ignore */ }
			try { await this._qr.clear(); } catch { /* ignore */ }
			this._qr = null;
		}
		if (stream) {
			try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
		}
		if (video) video.srcObject = null;
		this._unmountOverlay();
		this.requestUpdate();
	}

	private getInitials(name: string): string {
		const parts = (name || "").split(" ").filter(Boolean);
		if (parts.length === 0) return "?";
		return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
	}

	private _hasAlergias(a: string): boolean {
		return !!a && !SIN_ALERGIAS.includes(a.trim().toUpperCase());
	}

	render() {
		const r = this.result.value;
		return html`
			<div class="header">
				<h1>Escanear Asistencia</h1>
				<button class="back-btn" @click=${() => this.dispatchEvent(new CustomEvent("back-to-admin"))}>${iconArrowLeft()} Volver</button>
			</div>
			<div class="selector">
				<select .value=${this.selectedEvento.value} @change=${(e: Event) => this._onEventoChange(e)}>
					${this.eventos.value.map((e) => html`<option value=${e.id} ?selected=${e.id === this.selectedEvento.value}>${e.eventName}</option>`)}
				</select>
			</div>
			<div class="layout">
				<div class="scan-card">
					<button class="btn btn-primary" @click=${() => this.startCamera()}>${iconCamera()} Iniciar cámara</button>

					<details class="manual">
						<summary>¿No tenés cámara? Pegar token manualmente</summary>
						<div class="manual-row">
							<input
								type="text"
								autocomplete="off"
								autocapitalize="off"
								autocorrect="off"
								spellcheck="false"
								placeholder="Pegá el token QR acá…"
								.value=${this.scanInput.value}
								@input=${(e: Event) => { this.scanInput.value = (e.target as HTMLInputElement).value; }}
								@keydown=${(e: KeyboardEvent) => { if (e.key === "Enter") this._manualScan(); }}
							/>
							<button class="btn btn-secondary" @click=${() => this._manualScan()} ?disabled=${!this.scanInput.value.trim()}>Buscar</button>
						</div>
					</details>

					${r ? this._renderResult(r) : null}
				</div>

				<div class="stats">
					<h2>Estado del evento</h2>
					<div class="stat-row"><span class="stat-label">Inscritos</span><span class="stat-value blue">${this.stats.value.inscritos}</span></div>
					<div class="stat-row"><span class="stat-label">Con asistencia</span><span class="stat-value green">${this.stats.value.asistentes}</span></div>
					<div class="stat-row"><span class="stat-label">Pendientes</span><span class="stat-value orange">${this.stats.value.noAsistieron}</span></div>
				</div>
			</div>
		`;
	}

	private _renderResult(r: ScanResult) {
		if (r.status === "error" || r.status === "wrong_event") {
			return html`
				<div class="result-box result-error">
					<h3>${iconX()} ${r.status === "wrong_event" ? "QR de otro evento" : "Error"}</h3>
					<p>${r.error}</p>
					<div class="btn-row" style="margin-top:10px;">
						${r.status === "error" && this._lastToken
							? html`<button class="btn btn-primary" @click=${() => this._retry()}>Reintentar</button>`
							: null}
						<button class="btn btn-secondary" @click=${() => this.startCamera()}>${iconCamera()} Escanear otro</button>
					</div>
				</div>`;
		}
		if (r.status === "checked_in") {
			return html`
				<div class="result-box result-success">
					<h3>${iconCheck()} Asistencia registrada</h3>
					${this._renderPersona(r.persona)}
					<div class="btn-row" style="margin-top:10px;">
						<button class="btn btn-primary" @click=${() => this.startCamera()}>${iconCamera()} Escanear siguiente</button>
					</div>
				</div>`;
		}
		const isAlready = r.status === "already_checked_in";
		return html`
			<div class="result-box ${isAlready ? "result-warning" : "result-success"}">
				<h3>${isAlready ? html`${iconWarning()} Ya estaba registrado` : html`${iconCheck()} Persona verificada`}</h3>
				${this._renderPersona(r.persona)}
				${!isAlready
					? html`<div class="btn-row" style="margin-top:10px;">
							<button class="btn btn-primary" @click=${() => this._confirm()} ?disabled=${this.confirming.value}>
								${this.confirming.value ? "Confirmando…" : "Confirmar asistencia"}
							</button>
							<button class="btn btn-secondary" @click=${() => this._clearResult()} ?disabled=${this.confirming.value}>Cancelar</button>
						</div>`
					: html`<div class="btn-row" style="margin-top:10px;">
							<button class="btn btn-primary" @click=${() => this.startCamera()}>${iconCamera()} Escanear siguiente</button>
						</div>`}
			</div>`;
	}

	private _renderPersona(p: Persona | undefined) {
		if (!p || !p.name) return null;
		const alergias = this._hasAlergias(p.alergias);
		return html`
			<div class="persona-card">
				<div class="persona-head">
					<div class="persona-avatar">${this.getInitials(p.name)}</div>
					<div>
						<div class="persona-name">${p.name}</div>
						<div class="persona-sub">${[p.puesto, p.empresa].filter(Boolean).join(" · ") || "\u00a0"}</div>
					<div class="tags">
						${p.gafete ? html`<span class="tag gafete">Gafete ${p.gafete}</span>` : null}
						${p.tipoOrganizacion ? html`<span class="tag type">${p.tipoOrganizacion}</span>` : null}
						${p.esMiembroClam ? html`<span class="tag member">Miembro CLAM</span>` : null}
					</div>
					</div>
				</div>
				<div class="detail-grid">
					${p.email ? html`<div class="detail-row"><span class="detail-label">Email</span><span class="detail-value">${p.email}</span></div>` : null}
					${p.emailInstitucional ? html`<div class="detail-row"><span class="detail-label">Email institucional</span><span class="detail-value">${p.emailInstitucional}</span></div>` : null}
					${p.estado ? html`<div class="detail-row"><span class="detail-label">Estado</span><span class="detail-value">${p.estado}</span></div>` : null}
					${p.municipio ? html`<div class="detail-row"><span class="detail-label">Municipio</span><span class="detail-value">${p.municipio}</span></div>` : null}
				</div>
				${alergias ? html`<div class="alergias-alert">${iconWarning()}<span><strong>Alergia alimentaria:</strong> ${p.alergias} — revisar catering.</span></div>` : null}
			</div>
		`;
	}
}
